import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import Modal from 'components/ui/Modal';
import Skeleton from 'components/ui/skeleton';
import * as CollectionsModule from 'modules/collections';

interface Props {
  open: boolean;
  onClose: () => void;
  teamId: string;
  /** Collections already on the team — excluded from the list. */
  assignedCollectionIds: string[];
  onAssigned: () => void;
}

export function AssignCollectionDialog({
  open,
  onClose,
  teamId,
  assignedCollectionIds,
  onAssigned,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { collections, isLoading } = CollectionsModule.Hooks.useOwned({ enabled: open });
  const share = CollectionsModule.Hooks.useShare();

  const assigned = useMemo(() => new Set(assignedCollectionIds), [assignedCollectionIds]);
  const available = useMemo(
    () => collections.filter((c) => !assigned.has(c.id)),
    [collections, assigned],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return available.filter((c) => !q || c.name.toLowerCase().includes(q));
  }, [available, search]);

  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

  const reset = () => {
    setSelectedIds([]);
    setSearch('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) return;
    try {
      await Promise.all(
        selectedIds.map((id) =>
          share.mutateAsync({
            collectionId: id,
            type: 'team',
            targetId: teamId,
            access: 'view',
          }),
        ),
      );
      onAssigned();
      reset();
      onClose();
    } catch {
      // handled by hook toast
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title={t('teams.assign_collections_title')}>
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('collections.search_collections')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
          {isLoading ? (
            <div className="space-y-3 p-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : available.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t('teams.all_collections_assigned')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t('teams.no_collections_match')}
            </div>
          ) : (
            filtered.map((c) => (
              <label
                key={c.id}
                htmlFor={`assign-${c.id}`}
                className="flex cursor-pointer items-start gap-3 rounded-md p-2 transition-colors hover:bg-muted"
              >
                <Checkbox
                  id={`assign-${c.id}`}
                  checked={selectedIds.includes(c.id)}
                  onCheckedChange={() => toggle(c.id)}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{c.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('collections.items_count', { count: c.itemCount })}
                  </p>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button type="button" variant="outline" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button onClick={handleAssign} disabled={selectedIds.length === 0 || share.isPending}>
            {share.isPending
              ? t('teams.assigning')
              : selectedIds.length > 0
                ? t('teams.assign_n', { count: selectedIds.length })
                : t('teams.assign')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AssignCollectionDialog;
